import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, Loader2, X, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Resolve a stored relative URL (e.g. "/api/files/...") to an absolute URL
 * pointing to the backend, so <img> tags work from anywhere.
 */
export function resolveImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${BACKEND}${url}`;
  return url;
}

/**
 * Hero-image upload field. Lets admin either paste a URL or upload a file.
 *
 * Props:
 *  - value: current URL string
 *  - onChange: (url) => void
 *  - required: bool
 */
function ImageUploadField({ value, onChange, required = false }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/upload`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      onChange(data.url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const clearImage = () => {
    onChange('');
    setError('');
  };

  return (
    <div data-testid="image-upload-field">
      <div className="flex gap-2 items-stretch">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid="article-image-url-input"
          required={required}
          className="flex-1 px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
          placeholder="Paste an image URL or click UPLOAD →"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="image-upload-button"
          className="flex items-center gap-2 px-4 py-3 bg-[#E63946] text-white font-bold text-xs tracking-[0.2em] uppercase hover:bg-[#d32f3e] transition-colors disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              UPLOADING
            </>
          ) : (
            <>
              <Upload size={14} />
              UPLOAD
            </>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={handleFileChange}
          className="hidden"
          data-testid="image-upload-file-input"
        />
      </div>

      {value && (
        <div className="mt-3 relative inline-block" data-testid="image-preview">
          <img
            src={resolveImageUrl(value)}
            alt="Hero preview"
            className="max-h-40 border border-[#E5E5E5] object-cover"
          />
          <button
            type="button"
            onClick={clearImage}
            data-testid="image-clear-button"
            className="absolute -top-2 -right-2 w-6 h-6 bg-[#E63946] text-white rounded-full flex items-center justify-center hover:bg-[#d32f3e] shadow-md"
            aria-label="Clear image"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {error && (
        <div className="mt-2 text-xs text-[#E63946] font-semibold" data-testid="image-upload-error">
          {error}
        </div>
      )}

      <p className="text-xs text-[#8A8A8A] mt-2 flex items-center gap-1.5">
        <ImageIcon size={12} />
        Paste a URL or upload JPG / PNG / WebP / GIF (max 10 MB).
      </p>
    </div>
  );
}

export default ImageUploadField;
